import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import { usePermissions } from '@refinedev/core'
import { Alert, Breadcrumb, Button, Input, Space, Table } from 'antd'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { hasPermission } from '@/auth/capabilities'
import { isSystemRole } from '@/constants/roles'

import { RoleDeleteModal } from './components/RoleDeleteModal'
import { type BasicRole, useRoles } from './hooks/useRoles'
import { encodeRoleParam } from './routeParam'

export default function RolesListPage() {
  const navigate = useNavigate()
  const { roles, isLoading, error, refetch } = useRoles()
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canWrite = hasPermission(permissions, 'admin:roles:write')
  const [search, setSearch] = useState('')
  const [roleToDelete, setRoleToDelete] = useState<string>()

  const term = search.toLowerCase()
  const filtered = term
    ? roles.filter(
        (role) =>
          role.name.toLowerCase().includes(term) ||
          (role.description ?? '').toLowerCase().includes(term),
      )
    : roles

  const openRole = (name: string) => navigate(`/roles/${encodeRoleParam(name)}`)

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Roles' }]} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/roles/create')}
          >
            Create Role
          </Button>
        )}
      </div>

      <Input.Search
        placeholder="Search roles..."
        style={{ marginBottom: 16, maxWidth: 400 }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load roles."
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      )}

      <Table<BasicRole>
        dataSource={filtered}
        loading={isLoading}
        rowKey="name"
        pagination={false}
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: () => openRole(record.name),
        })}
        columns={[
          { title: 'Name', dataIndex: 'name', key: 'name' },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (description: string) => description || '—',
          },
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: BasicRole) => (
              <Space onClick={(e) => e.stopPropagation()}>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => openRole(record.name)}
                />
                {canWrite && !isSystemRole(record.name) && (
                  <Button
                    aria-label={`Delete ${record.name}`}
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setRoleToDelete(record.name)}
                  />
                )}
              </Space>
            ),
          },
        ]}
      />
      <RoleDeleteModal
        roleName={roleToDelete}
        onCancel={() => setRoleToDelete(undefined)}
        onDeleted={() => setRoleToDelete(undefined)}
      />
    </div>
  )
}
